import type { IEvaultApi } from './use-evault-api';
import type { IVaultDetail } from './types';

import { useCallback, useEffect, useRef, useState } from 'react';

import { EVAULT_API } from './use-evault-api';

/**
 * The four states a vault page can be in, named rather than derived from a pair
 * of nullable fields.
 *
 * `missing` and `error` are the distinction the old detail view did not draw at
 * all: a failed `GET /vaults/:id` left `data: null` and it skeletoned forever
 * (§2.7). They are also not the same answer — a vault that is not yours reads
 * "this vault isn't here", a service that did not respond reads "try again" —
 * and conflating them would tell someone their vault was gone because esign.api
 * was restarting.
 */
export type IVaultDetailState = 'loading' | 'ready' | 'missing' | 'error';

/**
 * One vault — `GET /vaults/:id`.
 *
 * The 404 is deliberate on the server side and worth restating here: the query
 * matches on `businessId` AND `ownerId`, so a colleague's vault, another
 * tenant's vault and a vault that never existed are all indistinguishable from
 * outside. The page says so in those words rather than guessing which it was.
 */
export const useVaultDetail = (api: IEvaultApi, id: string) => {
	const { get, vaultToken } = api;

	const [vault, setVault] = useState<IVaultDetail | null>(null);
	const [state, setState] = useState<IVaultDetailState>('loading');
	const [message, setMessage] = useState('');

	/* Same stale-response guard the drive uses: navigating between two vaults
	   issues two requests, and the slower one must not paint over the newer. */
	const ticket = useRef(0);

	const fetchVault = useCallback(async () => {
		if (!vaultToken || !id) return;
		const mine = (ticket.current += 1);
		setState('loading');

		const result = await get<IVaultDetail>(`${EVAULT_API.VAULTS}/${id}`);
		if (mine !== ticket.current) return;

		if (result.kind === 'ok' && result.data?._id) {
			setVault(result.data);
			setMessage('');
			setState('ready');
			return;
		}
		/* On the STATUS, not the wording. Matching "not found" in the message
		   would make this screen depend on a sentence the server is free to
		   rewrite. */
		if (result.kind !== 'ok' && result.status === 404) {
			setMessage('');
			setState('missing');
			return;
		}
		setMessage(result.kind === 'ok' ? '' : result.message);
		setState(result.kind === 'ok' ? 'missing' : 'error');
	}, [get, id, vaultToken]);

	/* THE VAULT'S ID AND WHETHER A BEARER EXISTS — not `fetchVault`, which is a
	   new function on every re-mint of the token. `useVaultToken` mints twice on
	   mount under StrictMode and again every ~14 minutes, and neither is a reason
	   to re-read a vault that has not changed. `refetch` stays the explicit way,
	   and rename/archive update from their own response instead. */
	const hasToken = !!vaultToken;
	useEffect(() => {
		void fetchVault();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasToken, id]);

	/* Rename and archive both answer with the whole vault (the server's `patch`
	   returns `this.get`), so the page updates from the response rather than
	   refetching — one fewer request, and no window where the head still shows
	   the old name. */
	const applyVault = useCallback((next: IVaultDetail) => {
		setVault(next);
		setState('ready');
	}, []);

	return { vault, state, message, refetch: fetchVault, applyVault };
};
